import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { CustomerData } from './types';
import { customerValidationSchema } from './validation';
import { useNomenclator } from '../../hooks';
import { PhoneConfirmationDialog } from './PhoneConfirmationDialog';

const RomanianFlag = () => (
  <svg className="w-6 h-4" viewBox="0 0 640 480">
    <g fillRule="evenodd" strokeWidth="1pt">
      <path fill="#00319c" d="M0 0h213.3v480H0z"/>
      <path fill="#ffde00" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#de2110" d="M426.7 0H640v480H426.7z"/>
    </g>
  </svg>
);

interface CustomerFormProps {
  onSubmit: (data: CustomerData) => void;
  initialValues?: Partial<CustomerData>;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ onSubmit, initialValues = {} }) => {
  const { preloadNomenclator } = useNomenclator();

  const formik = useFormik<CustomerData>({
    initialValues: {
      name: initialValues.name || '',
      email: initialValues.email || '',
      phone: initialValues.phone || '',
    },
    validationSchema: customerValidationSchema,
    onSubmit: () => {
      // Instead of submitting directly, show confirmation dialog
      setShowPhoneConfirmation(true);
    },
  });

  useEffect(() => {
    const preload = async () => {
      try {
        await preloadNomenclator();
      } catch (error) {
        console.error('Failed to preload nomenclator:', error);
      }
    };
    preload();
  }, [preloadNomenclator]);

  const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);

  const handlePhoneConfirm = () => {
    setShowPhoneConfirmation(false);
    onSubmit(formik.values);
  };

  const handlePhoneEdit = () => {
    setShowPhoneConfirmation(false);
    // Reset form submission state
    formik.setSubmitting(false);
    // Focus the phone input field
    const phoneInput = document.getElementById('phone');
    if (phoneInput) {
      phoneInput.focus();
    }
  };

  const handleCancel = () => {
    setShowPhoneConfirmation(false);
    formik.setSubmitting(false);
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-200">
          Nume și prenume
        </label>
        <input
          type="text"
          id="name"
          {...formik.getFieldProps('name')}
          className={`mt-1 block w-full rounded-md bg-gray-800/90 border-gray-700 text-white placeholder-gray-400
            shadow-sm focus:border-red-500 focus:ring-red-500 text-sm py-2 px-3 transition-colors
            hover:bg-gray-800 focus:bg-gray-800
            ${formik.touched.name && formik.errors.name ? 'border-red-500' : 'hover:border-gray-600'}`}
        />
        {formik.touched.name && formik.errors.name && (
          <p className="mt-1 text-xs text-red-500">{formik.errors.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-200">
          Email
        </label>
        <input
          type="email"
          id="email"
          {...formik.getFieldProps('email')}
          className={`mt-1 block w-full rounded-md bg-gray-800/90 border-gray-700 text-white placeholder-gray-400
            shadow-sm focus:border-red-500 focus:ring-red-500 text-sm py-2 px-3 transition-colors
            hover:bg-gray-800 focus:bg-gray-800
            ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'hover:border-gray-600'}`}
        />
        {formik.touched.email && formik.errors.email && (
          <p className="mt-1 text-xs text-red-500">{formik.errors.email}</p>
        )}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-200">
          Telefon
        </label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <RomanianFlag />
          </div>
          <input
            type="tel"
            id="phone"
            {...formik.getFieldProps('phone')}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '');
              
              // If empty, allow clearing the field
              if (value === '') {
                formik.setFieldValue('phone', '');
                return;
              }
              
              // If first digit is not 0, prepend 0
              if (value.length === 1 && value !== '0') {
                value = '0' + value;
              }
              
              // If second digit is not 7, make it 7
              if (value.length === 2 && value[0] === '0' && value[1] !== '7') {
                value = '07';
              }
              
              // Limit to 10 digits
              if (value.length <= 10) {
                formik.setFieldValue('phone', value);
              }
            }}
            className={`block w-full rounded-md bg-gray-800/90 border-gray-700 text-white placeholder-gray-400
              shadow-sm focus:border-red-500 focus:ring-red-500 text-sm py-2 pl-12 pr-3 transition-colors
              hover:bg-gray-800 focus:bg-gray-800
              ${formik.touched.phone && formik.errors.phone ? 'border-red-500' : 'hover:border-gray-600'}`}
            placeholder="07XX XXX XXX"
            maxLength={10}
          />
        </div>
        {formik.touched.phone && formik.errors.phone && (
          <p className="mt-1 text-xs text-red-500">{formik.errors.phone}</p>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting}
          className="inline-flex justify-center rounded-md border border-transparent bg-red-500 py-2 px-4 text-sm font-medium text-white 
            shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 
            disabled:opacity-50 transition-colors duration-200 ease-in-out w-full sm:w-auto"
        >
          Continuă
        </button>
      </div>
      </form>

      <PhoneConfirmationDialog
        isOpen={showPhoneConfirmation}
        phoneNumber={formik.values.phone}
        onConfirm={handlePhoneConfirm}
        onEdit={handlePhoneEdit}
        onCancel={handleCancel}
      />
    </>
  );
};
